import { formatIdr, formatUsd, calculateTopupQuote, type Topup } from '@ytclip/shared'
import { CheckCircle2, Clock, ExternalLink, QrCode, RefreshCw, Smartphone } from 'lucide-react'
import { useMemo, useState } from 'react'
import QRCode from 'react-qr-code'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/field.tsx'
import { LoadingBlock } from '@/components/ui/feedback.tsx'
import { useCreateTopup, useFeeRate, useTopupStatus } from './hooks.ts'
import { openPaymentPage } from './payment-page.ts'

export function TopupDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [amount, setAmount] = useState('')
  const [topup, setTopup] = useState<Topup | null>(null)

  const { data: feeRate, isPending } = useFeeRate()
  const createTopup = useCreateTopup()
  const status = useTopupStatus(topup?.id ?? null)

  const current = status.data ?? topup

  const quote = useMemo(() => {
    const usdAmount = Number.parseInt(amount, 10)

    if (!feeRate || !Number.isFinite(usdAmount) || usdAmount <= 0) return null

    return calculateTopupQuote({
      usdAmount,
      usdIdrRate: feeRate.usdIdrRate,
      platformFeePercent: feeRate.platformFeePercent,
    })
  }, [amount, feeRate])

  const outOfRange =
    quote && feeRate
      ? quote.usdAmount < feeRate.minTopupUsd || quote.usdAmount > feeRate.maxTopupUsd
      : false

  function close(next: boolean) {
    onOpenChange(next)

    if (!next) {
      setAmount('')
      setTopup(null)
    }
  }

  async function submit() {
    if (!quote || outOfRange) return

    const created = await createTopup.mutateAsync({ usdAmount: quote.usdAmount })
    setTopup(created)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={current ? 'Scan untuk membayar' : 'Top-up saldo'}
      description={
        current
          ? 'Buka aplikasi bank atau e-wallet, lalu scan kode di bawah.'
          : 'Masukkan jumlah kredit dalam USD.'
      }
    >
      {isPending ? <LoadingBlock /> : null}

      {!isPending && !current ? (
        <div className="space-y-5">
          <TextField
            label="Jumlah kredit"
            prefix="$"
            inputMode="numeric"
            placeholder="10"
            value={amount}
            onChange={(event) => setAmount(event.target.value.replace(/\D/g, ''))}
            hint={
              feeRate
                ? `Minimum $${feeRate.minTopupUsd}, maksimum $${feeRate.maxTopupUsd}. Hanya kelipatan dolar penuh.`
                : undefined
            }
            error={outOfRange ? 'Jumlah di luar batas yang diizinkan' : undefined}
          />

          {quote && !outOfRange ? (
            <dl className="rounded-md bg-surface-container-low p-4 text-body-md">
              <QuoteRow label="Subtotal" value={formatIdr(quote.idrAmount)} />
              <QuoteRow
                label={`Biaya platform (${quote.platformFeePercent}%)`}
                value={formatIdr(quote.platformFeeIdr)}
              />
              <div className="my-2 border-t border-outline-variant" />
              <QuoteRow label="Total bayar" value={formatIdr(quote.totalIdr)} emphasise />
              <p className="mt-2 text-label-sm text-on-surface-variant">
                Kurs {formatIdr(quote.usdIdrRate)} per USD
              </p>
            </dl>
          ) : null}

          <Button
            className="w-full"
            size="lg"
            disabled={!quote || outOfRange}
            loading={createTopup.isPending}
            onClick={() => void submit()}
            icon={<QrCode className="size-4" />}
          >
            Buat kode QRIS
          </Button>
        </div>
      ) : null}

      {current ? (
        <PaymentPanel
          topup={current}
          onDone={() => close(false)}
          onCheckNow={() => void status.checkNow()}
          checking={status.isFetching}
        />
      ) : null}
    </Dialog>
  )
}

function QuoteRow({
  label,
  value,
  emphasise,
}: {
  label: string
  value: string
  emphasise?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <dt className="text-on-surface-variant">{label}</dt>
      <dd className={emphasise ? 'text-title-lg font-medium text-on-surface' : 'text-on-surface'}>
        {value}
      </dd>
    </div>
  )
}

function PaymentPanel({
  topup,
  onDone,
  onCheckNow,
  checking,
}: {
  topup: Topup
  onDone: () => void
  onCheckNow: () => void
  checking: boolean
}) {
  if (topup.status === 'paid') {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <CheckCircle2 className="size-16 text-success" />
        <p className="text-title-lg font-medium text-on-surface">Pembayaran diterima</p>
        <p className="text-body-md text-on-surface-variant">
          Saldo kamu bertambah {formatUsd(topup.usdAmount)}.
        </p>
        <Button className="mt-2" onClick={onDone}>
          Selesai
        </Button>
      </div>
    )
  }

  // The invoice is still open but the gateway's dynamic QR (about ten minutes)
  // has lapsed. Its hosted page mints a fresh one, and polling carries on here.
  if (topup.status === 'pending' && !topup.qrString && topup.paymentUrl) {
    return (
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        <QrCode className="size-16 text-primary" />
        <p className="text-title-lg font-medium text-on-surface">Kode QR perlu diperbarui</p>
        <p className="text-body-md text-on-surface-variant">
          Kode QRIS hanya berlaku beberapa menit. Buka halaman pembayaran untuk kode baru senilai{' '}
          {formatIdr(topup.totalIdr)}. Saldo tetap masuk otomatis.
        </p>
        <Button
          className="mt-2 w-full"
          size="lg"
          onClick={() => openPaymentPage(topup.paymentUrl)}
          icon={<ExternalLink className="size-4" />}
        >
          Buka halaman pembayaran
        </Button>
        <Button
          variant="tonal"
          size="lg"
          className="w-full"
          loading={checking}
          onClick={onCheckNow}
          icon={<RefreshCw className="size-4" />}
        >
          Saya sudah bayar, cek sekarang
        </Button>
      </div>
    )
  }

  if (topup.status !== 'pending' || !topup.qrString) {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <Clock className="size-16 text-on-surface-variant" />
        <p className="text-title-lg font-medium text-on-surface">Kode sudah tidak berlaku</p>
        <p className="text-body-md text-on-surface-variant">
          Buat kode baru untuk melanjutkan pembayaran.
        </p>
        <Button className="mt-2" variant="tonal" onClick={onDone}>
          Tutup
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="mx-auto w-fit rounded-md bg-surface-container-lowest p-5 shadow-level-1">
        <QrisMark />
        <QRCode value={topup.qrString} size={200} bgColor="#FFFFFF" fgColor="#1C1B1F" />
      </div>

      <div className="text-center">
        <p className="text-headline-md font-medium text-on-surface">
          {formatIdr(topup.totalIdr)}
        </p>
        <p className="mt-1 text-body-md text-on-surface-variant">
          untuk {formatUsd(topup.usdAmount)} kredit
        </p>
      </div>

      <div className="rounded-md bg-surface-container-low p-4">
        <p className="flex items-center gap-2 text-label-md font-medium text-on-surface">
          <Smartphone className="size-4 text-primary" aria-hidden="true" />
          Bisa dibayar dari aplikasi apa saja
        </p>
        <p className="mt-1.5 text-label-sm leading-relaxed text-on-surface-variant">
          Mobile banking (BCA, Mandiri, BRI, BNI, dan lainnya) maupun e-wallet seperti GoPay,
          OVO, DANA, dan ShopeePay. Pilih menu scan QRIS di aplikasimu.
        </p>
      </div>

      <div className="flex items-center justify-center gap-2 rounded-full bg-surface-container-low px-4 py-3 text-label-md text-on-surface-variant">
        <span className="size-2 animate-pulse rounded-full bg-primary" aria-hidden="true" />
        Menunggu pembayaran…
      </div>

      <Button
        variant="tonal"
        size="lg"
        className="w-full"
        loading={checking}
        onClick={onCheckNow}
        icon={<RefreshCw className="size-4" />}
      >
        Saya sudah bayar, cek sekarang
      </Button>

      {topup.paymentUrl ? (
        <Button
          variant="text"
          className="w-full"
          onClick={() => openPaymentPage(topup.paymentUrl)}
          icon={<ExternalLink className="size-4" />}
        >
          Kode tidak bisa discan? Buka halaman pembayaran
        </Button>
      ) : null}

      <p className="text-center text-label-sm text-on-surface-variant">
        Saldo masuk otomatis setelah pembayaran terkonfirmasi. Dialog ini boleh ditutup.
      </p>
    </div>
  )
}

/**
 * Text mark rather than the official QRIS artwork, which is a licensed asset
 * this project does not ship. Drop the real file in and swap this out if the
 * licence allows.
 */
function QrisMark() {
  return (
    <p className="mb-3 text-center text-label-md font-bold tracking-[0.2em] text-primary">
      QRIS
    </p>
  )
}
